import type { Dispatch } from 'redux';
import { setGuestToken } from './features/advisor/advisorSlice';
import type { RootState } from './store';
import { getValidTokenForChat } from './utils/chatTokenUtils';
import { isTokenExpiredOrExpiringSoon } from './utils/jwtUtils';
import { getClaimIntakeApiConfig } from './claimIntakeApiConfig';
import type { GuestTokenLike } from './claimIntakeApiConfig';

export type ClaimIntakeStoreRef = {
  getState: () => RootState;
  dispatch: Dispatch;
};

let claimIntakeStoreRef: ClaimIntakeStoreRef | null = null;

/** Registered by `ClaimIntakePage` so stream/REST calls can refresh tokens. */
export function registerClaimIntakeStoreRef(ref: ClaimIntakeStoreRef | null): void {
  claimIntakeStoreRef = ref;
}

function resolveStoreRef(
  override?: Partial<ClaimIntakeStoreRef>
): ClaimIntakeStoreRef | null {
  if (override?.getState && override?.dispatch) {
    return { getState: override.getState, dispatch: override.dispatch };
  }
  return claimIntakeStoreRef;
}

/**
 * Returns a valid Bearer for claim-intake streams and REST.
 * Mirrors chat: Auth0 refresh when logged in; silent guest refresh when not.
 */
export async function getValidClaimIntakeBearerToken(
  currentToken: string,
  options?: Partial<ClaimIntakeStoreRef> & { bufferSeconds?: number }
): Promise<string> {
  if (!currentToken?.trim()) {
    throw new Error('No claim-intake bearer token available');
  }

  const bufferSeconds = options?.bufferSeconds ?? 300;
  if (!isTokenExpiredOrExpiringSoon(currentToken, bufferSeconds)) {
    return currentToken;
  }

  const store = resolveStoreRef(options);
  const state = store?.getState();
  const isAuthenticated = Boolean(state?.auth?.user?.isAuthenticated);

  if (isAuthenticated && store?.dispatch) {
    return getValidTokenForChat(currentToken, bufferSeconds, store.dispatch, {
      isAuthenticated: true,
    });
  }

  const refresh = getClaimIntakeApiConfig().fetchLatestGuestToken;
  if (!refresh) {
    return currentToken;
  }

  try {
    const newToken = await refresh();
    if (!newToken?.trim()) {
      return currentToken;
    }

    if (newToken !== currentToken && store?.dispatch && store?.getState) {
      const existing = store.getState().advisor?.guestToken as GuestTokenLike;
      const apiKey =
        existing && typeof existing === 'object' && existing.apiKey
          ? existing.apiKey
          : undefined;
      store.dispatch(
        setGuestToken({
          apiToken: newToken,
          ...(apiKey ? { apiKey } : {}),
        })
      );
    }

    return newToken;
  } catch {
    return currentToken;
  }
}
